import { useEffect, useState } from "react";
import { Ban, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loading, EmptyState } from "@/components/States";
import { api, formatError } from "@/lib/api";

export default function AdminContacts() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/employer-contacts").then((r) => setD(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);
  const block = async (email) => {
    try { await api.post("/admin/blocked-senders", { email }); toast.success(`${email} blocked`); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const unblock = async (email) => {
    try { await api.delete(`/admin/blocked-senders/${encodeURIComponent(email)}`); toast.success(`${email} unblocked`); load(); } catch (e) { toast.error(formatError(e)); }
  };
  if (!d) return <Loading />;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-3">Read-only list of messages employers sent to learners. Blocked senders' future messages are silently discarded.</p>
      {d.blocked.length > 0 && (
        <div data-testid="contacts-blocked" className="flex flex-wrap items-center gap-2 rounded-xl border border-red-100 bg-red-50/60 p-3 text-xs">
          <span className="font-medium text-red-800">Blocked:</span>
          {d.blocked.map((e) => (
            <span key={e} className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 text-red-700">{e}
              <button data-testid={`contacts-unblock-${e}`} onClick={() => unblock(e)} className="text-ink-3 hover:text-ink-2" aria-label={`Unblock ${e}`}><Undo2 className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      {d.items.length === 0 ? <EmptyState testId="contacts-empty" title="No employer messages yet" text="Messages sent from learner showcases will appear here." /> : (
        <div data-testid="contacts-list" className="flex flex-col gap-3">
          {d.items.map((m) => (
            <div key={m.id} data-testid={`contact-item-${m.id}`} className="rounded-xl border border-line bg-surface p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-ink">{m.sender_name}{m.company && <span className="font-normal text-ink-3"> · {m.company}</span>}</p>
                  <p className="text-sm text-ink-2">{m.sender_email} → {m.learner_name} (/learner/{m.learner_handle})</p>
                  <p className="text-xs text-ink-3">{new Date(m.created_at).toLocaleString()} · email {m.email_status} · {m.read ? "read" : "unread"}</p>
                </div>
                {m.blocked ? <span data-testid={`contact-blocked-${m.id}`} className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">Sender blocked</span> : (
                  <Button size="sm" variant="outline" data-testid={`contact-block-${m.id}`} onClick={() => block(m.sender_email)} className="rounded-full border-red-200 text-red-700 hover:bg-red-50"><Ban className="mr-1.5 h-3.5 w-3.5" />Block sender</Button>
                )}
              </div>
              <p className="mt-3 whitespace-pre-wrap rounded-lg bg-canvas-2 p-3 text-sm text-ink-2">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
